// ---------------------------------------------------------------------------
// HDDN LAB marketing site — fallback content.
//
// Everything here is a DEFAULT. At runtime `useSiteContent` merges the matching
// `site_content` sections from /api/site-content over the top, so the owner can
// correct any of it from the admin Website editor without a deploy. That mirrors
// the pattern already used for packages_config.
//
// Two rules held throughout:
//   - Prices are proposed and published. They are the owner's to set and are
//     trivially corrected; hiding them costs leads.
//   - Physical facts are never invented. Square footage, dimensions and ceiling
//     height live in `studioSpecs` as empty strings and render nothing until
//     someone fills them in. A fabricated square footage is a claim a renter can
//     measure with a tape.
// ---------------------------------------------------------------------------

import { CONTACT_EMAIL } from '../contact';

export const STUDIO = {
  name: 'HDDN LAB',
  street: '72 Centre Avenue',
  city: 'New Rochelle',
  state: 'NY',
  zip: '10801',
  email: CONTACT_EMAIL,
  instagram: 'https://instagram.com/hddn.lab',
  tiktok: 'https://tiktok.com/@hddn.lab',
  handle: '@hddn.lab',
  tagline: 'Just Create',
  openHour: 8,
  closeHour: 20,
  hoursLabel: '8 AM – 8 PM, seven days',
  maxGuests: 15,
};

// The 13 months of Downtown New Rochelle social. Verbatim, and the centrepiece
// of the content-side argument. Ordered deliberately: the catchment share leads,
// because a local client cares more that the audience genuinely lives here than
// that it is large.
export const CASE_STUDY = {
  client: 'Downtown New Rochelle',
  window: 'June 22, 2025 – July 29, 2026',
  months: 13,
  headline: [
    { n: '43.1%', k: 'of the audience is in New Rochelle', note: 'The catchment, not a vanity number' },
    { n: '1.81M', k: 'total reach', note: '905,544 owned + 904,563 earned' },
    { n: '855', k: 'pieces published', note: '638 original + 217 amplified' },
    { n: '1.59M', k: 'views', note: 'Across 13 months' },
  ],
  detail: [
    { n: '36,166', k: 'engagements' },
    { n: '2,216', k: 'new followers added' },
    { n: '55', k: 'local businesses tagged the feed', note: 'In 178 of their own posts' },
    { n: '2,494', k: 'average reach per reel', note: 'vs 858 for static — nearly 3×' },
    { n: '5,747', k: 'Instagram followers', note: 'Current audience' },
    { n: '3,474', k: 'Facebook followers', note: 'Current audience' },
  ],
};

// What gets measured from month one, for any account. The case study proves
// production capacity and a local audience — so the reporting names concrete
// signals and never promises an outcome nobody has measured. The one rule:
// general on the page, specific in the report.
export const REPORTING = [
  'Profile visits and direction/website taps — the closest signal to someone showing up',
  'Saves and shares, which is how a post actually travels between people',
  'Reach split into owned and earned, so you can see what the neighbourhood amplified',
  'Inquiries attributed to social — tours, bookings, orders, walk-ins — tracked from month one',
  'Post-level performance, so next month is built on what worked this month',
];

// Retainers publish one number: the floor. Scope above it — how many locations,
// how many shoot days, how much community management — varies enough that
// pricing it on a page would be guesswork, so it gets discussed instead.
//
// Keeping a single honest floor still wins the comparison the competitive
// research turned up: nobody else in this category publishes a figure at all.
export const RETAINER = {
  startingPrice: 2500,
  cadence: '/mo',
  note: 'Monthly, no long lock-in. Most clients keep their own accounts and drop our content in; posting it for you is an add-on. Studio time for shoot days is included.',
  included: [
    'Shoot days — your space, your product, your people, the block',
    'Reels cut first, because they reach roughly three times what a static post does',
    'Photos and graphics from the same day, sized for every platform',
    'Everything delivered ready to post: edited, coloured, captioned',
    'A monthly report on what reached and what converted',
  ],
  // What we actually ask about on the first call, in the order we ask it.
  shapes: [
    'How many locations, properties or brands you need covered',
    'How many shoot days a month you actually need',
    'Photo, video, or both',
    'Whether you want us to post it for you — that is the add-on',
    'Whether this is one push or an ongoing month',
  ],
};

// One-off work, for someone who needs a single thing rather than a monthly
// retainer. Names only — no prices.
//
// These four are the real services, taken from the preset list in the admin
// à la carte tab (src/crm-alacarte-tab.jsx). Their internal rates stay
// internal: those are line items billed alongside a separate talent cost, so
// the number next to "Full Event Coverage" is not what a client pays. Putting
// it on the page would be wrong, not merely conservative.
export const ALA_CARTE = {
  intro: 'Not every job is a monthly retainer. If you need one specific thing — an event covered, a single video made — tell us what it is and we will scope it.',
  services: [
    { name: 'Full event coverage', d: 'A launch, a resident event, an open house. We shoot it and you get the edit.' },
    { name: 'Special campaign clips', d: 'A short run of pieces built around one push, without signing up for the month.' },
    { name: 'Hi-res video shoot', d: 'One properly produced video — a tour, a spot, a brand piece.' },
    { name: 'Additional campaign', d: 'Extra work layered on top of an existing retainer.' },
    { name: 'We post it for you', d: 'Scheduling and publishing on your own accounts, on top of a retainer. The add-on, not the default.' },
  ],
  foot: 'Priced on scope, not from a menu — it depends on the day, the crew and what you need back.',
};

// Full Studio block rates. The $100/hr and its 3-hour minimum are real and
// already live; the two longer blocks are proposed and CMS-editable.
// Sense-check against the category: half day works out at $95/hr, full day at
// $87.50/hr — a conventional discount curve.
//
// There is deliberately no event rate. Event and coverage work varies too much
// to price on a page, so the studio page names what the room is good for and
// routes to a conversation instead of to a number.
export const STUDIO_RATES = [
  { id: 'hourly', label: 'Hourly', sub: '3-hour minimum', price: 100, unit: '/hr', foot: '$300 minimum' },
  { id: 'half-day', label: 'Half day', sub: '4 hours', price: 380, unit: '' },
  { id: 'full-day', label: 'Full day', sub: '8 hours', price: 700, unit: '' },
];

// Empty by design. Rendered only when filled in from the admin editor.
export const STUDIO_SPECS = {
  squareFeet: '',
  ceilingHeight: '',
  dimensions: '',
  naturalLight: '',
};

export const STUDIO_USES = {
  'full-studio': [
    'Photo and portrait sessions',
    'Video shoots and interviews',
    'Content days — a month of posts in an afternoon',
    'Lookbooks, product and flat-lay',
    'Workshops and classes',
    'Listening sessions, pop-ups and small events',
    'DJ sets',
  ],
  podcast: [
    'Video podcasts, solo or panel',
    'Audio-only episodes',
    'Interview series',
    'Clip-first social recordings',
  ],
};

// The strongest answer to "why not just book in the city". Currently this only
// reaches a renter in the email sent AFTER they book.
export const GETTING_HERE = {
  intro: 'We are not a street-level studio. Look for the tunnel entrance next to 72 Centre Avenue, walk through, and our door is at the end, marked HDDN.',
  transit: [
    'Metro-North — New Rochelle Station, 5–10 minute walk',
    'Bee-Line bus — routes 1, 8, 15 and 23 stop near Centre Ave',
  ],
  parking: [
    { lot: 'Skyline Garage', where: '26 Division St', walk: '3 min', note: 'Closest and easiest' },
    { lot: '360 Huguenot Garage', where: '360 Huguenot St', walk: '3 min' },
    { lot: 'Prospect Street Public Lot', where: '35 Prospect St', walk: '3 min' },
  ],
  parkingNote: 'Street parking is free after 7 PM every day and free all day Sunday — the meters stop at 7. There is no on-site parking, but you are welcome to pull up at the entrance to drop gear and passengers.',
};

// Facts a renter scans for before booking. All of these already exist in the
// FAQs, policies and rules — they are just buried in an accordion today.
export const GOOD_TO_KNOW = [
  { t: 'Hours', d: `${STUDIO.hoursLabel}. Setup and breakdown happen inside your booked time.` },
  { t: 'Capacity', d: `Up to ${STUDIO.maxGuests} people per booking. Tell us the headcount when you book.` },
  { t: 'Booking', d: 'Book online. Sessions 48 hours out with payment are confirmed automatically — no waiting on a reply.' },
  { t: 'Payment', d: 'Card, Venmo, PayPal or Zelle. Paid in full at booking.' },
  { t: 'Cancellation', d: 'Full refund 7+ days out, or within 24 hours of booking. 50% between 7 days and 24 hours. No refund inside 24 hours.' },
  { t: 'Your own gear', d: 'Bring whatever you like. Glitter, paint, powder, smoke and other hard-to-clean materials need written approval first.' },
  { t: 'Overtime', d: 'Charged at the hourly rate for your booking type, and it needs pre-approval.' },
  { t: 'Insurance', d: 'Not required for individual creators, though recommended. A COI is required for large productions, commercial shoots and outside equipment rentals.' },
  { t: 'Keeping it clean', d: 'Put furniture and gear back where you found it. A $100 cleaning fee applies if the studio is left in disarray.' },
  { t: 'No smoking', d: 'None of any kind, anywhere in the building. It carries a $250 fine and ends the session.' },
];

export const SITE_COPY = {
  heroEyebrow: 'New Rochelle, NY',
  heroTitle: 'A space where creativity thrives.',
  heroLede: 'HDDN LAB is a production studio in downtown New Rochelle. We make content for your social, we shoot portraits, and we rent the room. Pick the door you need.',
  forkNote: 'Three doors, one building.',
  doors: [
    {
      id: 'content',
      href: '/content',
      k: 'Content creation',
      h: 'We make your content',
      p: 'Video and photo for your social, on a monthly retainer. Shot here or at your place, delivered ready to post. We can post it for you too.',
      go: 'See the work and the pricing',
    },
    {
      id: 'photographer',
      href: '/photographer',
      k: 'Book a photographer',
      h: 'Get your photos taken',
      p: 'Portraits, headshots, product, families, brand. Shot in the studio or on location by one of our photographers.',
      go: 'Request a session',
    },
    {
      id: 'studio',
      href: '/studio',
      k: 'Rent the studio',
      h: 'Make it yourself',
      p: 'The studio by the hour or the day. Photo, video, podcast, events. Rates published, calendar open.',
      go: 'See the space and the rates',
    },
  ],
  loop: 'The studio is why the content work is real — you can stand in the room it comes out of. The content work is why the studio is professional — you can see what gets made here.',
  aboutTitle: 'Hidden for a reason.',
  aboutBody: [
    'HDDN LAB is a creative studio tucked into downtown New Rochelle. Behind an unassuming tunnel entrance on Centre Avenue you will find a fully-equipped space built for photographers, content creators, podcasters and brands.',
    'We are more than a room for rent. We are a community of local creatives — and a full production team when you need one, from studio sessions to social content to podcast production.',
  ],
  localTitle: 'The agency pitching you is not from here.',
  localBody: 'Most of the agencies bidding on marketing work in Westchester are pitching from Boston or midtown, flying a crew in for the day and calling it local. We are eleven minutes from the train, in the downtown your customers already walk through, and we have run its social accounts for the last thirteen months. When something happens on Main Street on a Tuesday, we are already there.',
  // Buildings are the audience we are actively courting right now, so they get
  // a clearly-scoped section — not the whole page. Everything in it stays
  // inside what the numbers support: attention and inquiries, never leases.
  buildings: {
    eyebrow: 'A note for property teams',
    title: 'Leasing a building right now?',
    lede: 'A lot of our current work is with new residential buildings, and the retainer fits lease-up well: shoot days on site, reels of units and amenities, neighbourhood content people actually share, and tour inquiries tracked from month one.',
    stat: 'More than 80% of renters research a community on social before they book a tour.',
    statNote: 'Which is why an empty or abandoned feed is not a neutral outcome for a building in lease-up. It is a closed door.',
    cta: 'Talk to us about your lease-up',
  },
  month: [
    { w: 'Week 1', t: 'Shoot day', d: 'We come to you. Your space, your product, your people, the block. One day on site produces the month.' },
    { w: 'Week 2', t: 'Edit and publish', d: 'Reels cut first — they reach roughly three times what a static post does. Captions written like a person who lives here wrote them.' },
    { w: 'Week 3', t: 'Neighbourhood', d: 'Content about what is actually around you. This is what the businesses and people nearby share on, and it is where earned reach comes from.' },
    { w: 'Week 4', t: 'Report and plan', d: 'What reached, what converted, what we are doing next month. Sent, not buried in a dashboard.' },
  ],
};

export const SEO_BLURB = {
  content: 'Content creation for social media: video and photo on a monthly retainer for local businesses, brands and residential buildings in New Rochelle, Westchester and the NY/NJ metro. Local team, published pricing, real numbers.',
  studio: 'Photo, video and podcast studio rental in New Rochelle, Westchester. $100/hr, published day rates, gear included, five minutes from Metro-North.',
};

// Book a photographer. The quote form and its admin inbox already existed at
// /photographer; this is the copy that puts a real door in front of it. The
// types mirror the form's own project-type list so the request lands in the
// inbox with the same value the admin already knows how to read.
export const PHOTOGRAPHY = {
  eyebrow: 'Book a photographer',
  title: 'Get your photos taken.',
  lede: 'Portraits, headshots, product, families, brand. Shot in the studio on Centre Avenue or wherever you need us, by one of our in-house photographers.',
  types: [
    { id: 'portrait', name: 'Portraits', d: 'You, your family, your dog. Studio light or natural light, your call.' },
    { id: 'headshots', name: 'Headshots', d: 'For the profile, the bio, the deck. Clean, quick, consistent across a whole team.' },
    { id: 'product', name: 'Product', d: 'On white, on a set, in someone\'s hands. Sized for the shop and the feed.' },
    { id: 'brand', name: 'Brand and lifestyle', d: 'Your people doing the thing you do, in the place you do it.' },
    { id: 'editorial', name: 'Editorial and lookbook', d: 'A collection, a season, a story told across a set of frames.' },
    { id: 'event', name: 'Events', d: 'A launch, an opening, a party. We cover it and you get the edit.' },
    { id: 'music', name: 'Music and artist promo', d: 'Press shots, cover art, the pictures that go with the release.' },
    { id: 'content', name: 'Content for social', d: 'A stack of photos for the month, shot in one session.' },
  ],
  steps: [
    { t: 'Tell us what you need', d: 'The form below. References, moodboards and rough ideas all help. Two minutes.' },
    { t: 'We come back with a quote', d: 'Within one to two business days, with a photographer matched to the job and a couple of questions.' },
    { t: 'Shoot day', d: 'In the studio or on location. Edited photos delivered after, sized for wherever they are going.' },
  ],
  note: 'Priced per session on what you actually need, not from a menu. If you have a budget in mind, tell us and we will work to it.',
};
